import type { Workspace } from '@/../../backend/src/db/types'
import { api } from './client'

type HexColor = `#${string}`

interface WorkspaceSettings {
  allowCandidatePortal: boolean
  minimumReferences: number
  autoReminders: boolean
}

export interface CreateWorkspaceData {
  name: string
  description?: string | null
  logoUrl?: string | null
  primaryColor?: HexColor | null
  settings?: WorkspaceSettings
}

export interface UpdateWorkspaceData {
  name?: string
  description?: string | null
  logoUrl?: string | null
  primaryColor?: HexColor | null
  active?: boolean
  settings?: WorkspaceSettings
}

export interface WorkspaceWithDetails extends Workspace {
  memberCount: number
  creator: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
}

export interface WorkspaceMember {
  id: string
  userId: string
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
    profilePictureUrl: string | null
    status?: string
    lastLoginAt?: Date | string | null
  }
  role: string
  createdAt: Date | string
  lastAccessedAt: Date | string | null
  active: boolean
}

export interface WorkspaceMembership {
  workspaceId: string
  workspaceName: string
  role: string
  permissions: string[]
  active: boolean
  lastAccessedAt: Date | string | null
}

export interface GetWorkspacesOptions {
  /** If true, admins will receive all company workspaces regardless of membership */
  all?: boolean
}

export const workspacesApi = {
  // Get all workspaces for the current user's company
  async getWorkspaces(options?: GetWorkspacesOptions): Promise<WorkspaceWithDetails[]> {
    const params = new URLSearchParams()
    if (options?.all) {
      params.append('all', 'true')
    }
    const queryString = params.toString()
    const url = queryString ? `/workspaces?${queryString}` : '/workspaces'
    const response = await api.get(url)
    return response.data.workspaces
  },

  // Get a single workspace by ID
  async getWorkspace(id: string): Promise<WorkspaceWithDetails> {
    const response = await api.get(`/workspaces/${id}`)
    return response.data.workspace
  },

  // Create a new workspace
  async createWorkspace(data: CreateWorkspaceData): Promise<WorkspaceWithDetails> {
    const response = await api.post('/workspaces', data)
    return response.data.workspace
  },

  // Update a workspace
  async updateWorkspace(id: string, data: UpdateWorkspaceData): Promise<WorkspaceWithDetails> {
    const response = await api.put(`/workspaces/${id}`, data)
    return response.data.workspace
  },

  // Delete a workspace
  async deleteWorkspace(id: string): Promise<void> {
    await api.delete(`/workspaces/${id}`)
  },

  // Get workspace members
  async getWorkspaceMembers(id: string): Promise<WorkspaceMember[]> {
    const response = await api.get(`/workspaces/${id}/members`)
    return response.data.members
  },

  // Get current user's membership in a workspace
  async getWorkspaceMembership(id: string): Promise<WorkspaceMembership> {
    const response = await api.get(`/workspaces/${id}/membership`)
    return response.data.membership
  },
}
